import { statusToneAccent, type StatusMeta } from '@/components/status-tone';
import type { KitLinkState } from '@shared/kit-release';
import type { RunPause } from '@shared/run-pause';
import type {
  CheckoutRequestStatus,
  CheckoutStatus,
  ProjectRole,
  RunStatus,
  RunStep,
  RunStepStatus,
  RunSummary,
  SecretImportOutcome,
  SecretImport,
  SecretImportStatus,
} from '@shared/types/factory';

/** How the factory shows run, step, checkout and machine statuses, and role names. */

export const RUN_STATUS_META: Record<RunStatus, StatusMeta> = {
  queued: { label: 'Queued', tone: 'neutral' },
  running: { label: 'Running', tone: 'info' },
  waiting: { label: 'Waiting', tone: 'warning' },
  succeeded: { label: 'Succeeded', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

export const STEP_STATUS_META: Record<RunStepStatus, StatusMeta> = {
  pending: { label: 'Pending', tone: 'neutral' },
  running: { label: 'Running', tone: 'info' },
  waiting: { label: 'Waiting', tone: 'warning' },
  succeeded: { label: 'Succeeded', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  skipped: { label: 'Skipped', tone: 'neutral' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

export const CHECKOUT_STATUS_META: Record<CheckoutStatus, StatusMeta> = {
  suggested: { label: 'Suggested', tone: 'warning' },
  confirmed: { label: 'Confirmed', tone: 'success' },
  ignored: { label: 'Ignored', tone: 'neutral' },
};

/** T-52: a confirmed checkout's links to the kit, as its machine's agent reports them. */
export const KIT_LINK_META: Record<KitLinkState, StatusMeta> = {
  linked: { label: 'Linked', tone: 'success' },
  // Only from agents before SF-T-179, which left a checkout without the .gitignore links block alone.
  'no-block': { label: 'Agent too old', tone: 'warning' },
  'kit-repo': { label: 'Carries the kit', tone: 'neutral' },
  devkit: { label: 'agent-devkit', tone: 'neutral' },
  missing: { label: 'Folder gone', tone: 'neutral' },
  error: { label: 'Link failed', tone: 'danger' },
};

/** A machine checkout request (project settings › Checkouts). */
export const CHECKOUT_REQUEST_STATUS_META: Record<CheckoutRequestStatus, StatusMeta> = {
  pending: { label: 'Waiting for machine', tone: 'warning' },
  running: { label: 'Checking out', tone: 'info' },
  done: { label: 'Checked out', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
};

export const SECRET_IMPORT_STATUS_META: Record<SecretImportStatus, StatusMeta> = {
  pending: { label: 'Waiting for machine', tone: 'warning' },
  running: { label: 'Reading files', tone: 'info' },
  done: { label: 'Done', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
};

/** Still waiting on the machine's answer. */
export const secretImportBusy = (i: SecretImport) => i.status === 'pending' || i.status === 'running';

/** What happened to a variable found in a machine's file, as a group heading. */
export const SECRET_IMPORT_OUTCOME_META: Record<SecretImportOutcome, StatusMeta> = {
  imported: { label: 'Imported', tone: 'success' },
  same: { label: 'Already set, same value', tone: 'neutral' },
  kept: { label: 'Already set, different value (kept)', tone: 'warning' },
  conflict: { label: 'Differs from an earlier file (skipped)', tone: 'warning' },
  invalid_name: { label: 'Not an UPPER_SNAKE_CASE name (skipped)', tone: 'neutral' },
  empty: { label: 'Empty (skipped)', tone: 'neutral' },
  too_long: { label: 'Too long (skipped)', tone: 'danger' },
};

export const MACHINE_ONLINE_META = {
  online: { label: 'Online', tone: 'success' },
  offline: { label: 'Offline', tone: 'neutral' },
  revoked: { label: 'Revoked', tone: 'danger' },
} satisfies Record<string, StatusMeta>;

export const ROLE_LABELS: Record<ProjectRole, string> = {
  viewer: 'Viewer',
  member: 'Member',
  maintainer: 'Maintainer',
};

/** Runs that should catch the eye in a list: in motion or in trouble. Succeeded and cancelled runs stay plain. */
const ACCENTED_RUN_STATUSES: readonly RunStatus[] = ['queued', 'running', 'waiting', 'failed'];

/** The status accent for a run's row (`row`) or list item (`item`), or undefined for a calm run. */
export function runAccent(status: RunStatus): { row: string; item: string } | undefined {
  if (!ACCENTED_RUN_STATUSES.includes(status)) return undefined;
  const { row, item } = statusToneAccent(RUN_STATUS_META[status].tone);
  return { row, item };
}

/** A run that hasn't finished: its page keeps polling. */
export function isRunActive(status: RunStatus): boolean {
  return status === 'queued' || status === 'running' || status === 'waiting';
}

/** SF-T-209: a live run that's waiting for people (its agent finished a turn): "Live, waiting for you". */
export const RUN_PARKED_META: StatusMeta = { label: 'Live, waiting for you', tone: 'warning' };
/** SF-T-266: a parked live run whose thread is at work anyway (someone wrote to it in T3 Code), until the run follows. */
export const RUN_THREAD_WORKING_META: StatusMeta = { label: 'Working', tone: 'info' };
/** SF-T-302: a suggestions-first run that's waiting for people's picks on its suggestions (or answers to its questions). */
export const RUN_PICKS_META: StatusMeta = { label: 'Waiting for your picks', tone: 'warning' };
/** SF-T-209: the chip on a live run that's still going (beside its status, as Feedback only is). */
export const LIVE_RUN_META: StatusMeta = { label: 'Live', tone: 'info' };
/** SF-T-302: the chip on a suggestions-first run that's still going. */
export const SUGGESTIONS_FIRST_RUN_META: StatusMeta = { label: 'Suggestions first', tone: 'info' };

/** SF-T-209: the status a run shows: a parked live run's own (SF-T-302: a suggestions-first one's), else its status's. */
export function runStatusMeta(run: Pick<RunSummary, 'status' | 'live_parked_at'> & { pause?: RunPause | null }): StatusMeta {
  if (!run.live_parked_at || !isRunActive(run.status)) return RUN_STATUS_META[run.status];
  return parkedRunMeta(run.pause);
}

/** SF-T-302: a parked run's status by how it waits: Waiting for your picks (suggestions first), else Live, waiting for you. */
export function parkedRunMeta(pause: RunPause | null | undefined): StatusMeta {
  return pause === 'suggestions first' ? RUN_PICKS_META : RUN_PARKED_META;
}

/** SF-T-302: the chip beside a live run's status while it's going: Suggestions first or Live. */
export function liveRunMeta(run: Pick<RunSummary, 'pause'>): StatusMeta {
  return run.pause === 'suggestions first' ? SUGGESTIONS_FIRST_RUN_META : LIVE_RUN_META;
}

/** SF-T-209: the task's newest run, when it's live and still going (Wrap up replaces Run while it is). */
export function activeLiveRun(runs: readonly RunSummary[] | undefined): RunSummary | null {
  const newest = runs?.[0];
  return newest && newest.live && isRunActive(newest.status) ? newest : null;
}

export interface RunProgress {
  /** 0 to 100. */
  percent: number;
  /** "Step 2 of 5 · Build", or how it ended. */
  label: string;
  tone: StatusMeta['tone'];
}

/**
 * How far a run has got, from its finished steps. A step that's going counts as half done, so a
 * run on its first step already shows a sliver.
 */
export function runProgress(run: Pick<RunSummary, 'status' | 'steps_done' | 'steps_total' | 'current_step' | 'current_step_name'>): RunProgress {
  const { status, steps_done: done, steps_total: total } = run;
  const tone = RUN_STATUS_META[status].tone;
  if (!isRunActive(status)) {
    return { percent: 100, label: RUN_STATUS_META[status].label, tone };
  }
  if (!total) return { percent: 0, label: RUN_STATUS_META[status].label, tone };
  const going = status === 'queued' ? 0 : 0.5;
  const percent = Math.min(100, Math.round(((Math.min(done, total) + going) / total) * 100));
  const step = run.current_step_name ?? run.current_step;
  const position = `Step ${Math.min(done + 1, total)} of ${total}`;
  const label = status === 'queued' ? 'Queued' : step ? `${position} · ${step}` : position;
  return { percent, label: status === 'waiting' ? `${label} (waiting)` : label, tone };
}

/**
 * SF-T-278: how far several runs going at once have got together (a parent's sub-tasks' runs): their steps done
 * (the step going counts half, as in runProgress) over all their steps, so a short run doesn't count as much as a
 * long one. Coloured as running while any is, else waiting while any waits. Null with none.
 */
export function runsProgress(runs: ReadonlyArray<Parameters<typeof runProgress>[0]>): RunProgress | null {
  if (!runs.length) return null;
  const each = runs.map(runProgress);
  let done = 0;
  let total = 0;
  for (const run of runs) {
    if (!run.steps_total) continue;
    if (!isRunActive(run.status)) done += run.steps_total;
    else done += Math.min(run.steps_done, run.steps_total) + (run.status === 'queued' ? 0 : 0.5);
    total += run.steps_total;
  }
  const percent = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const status = runs.some((r) => r.status === 'running') ? 'running' : runs.some((r) => r.status === 'waiting') ? 'waiting' : runs[0].status;
  const label = runs.length === 1 ? each[0].label : `${runs.length} runs, ${percent}% together`;
  return { percent, label, tone: RUN_STATUS_META[status].tone };
}

/** R-12 */
export function runRef(number: number): string {
  return `R-${number}`;
}

/** T3's access levels, lowest first (the agent's token ceiling and a flow's runtimeMode). */
export const T3_RUNTIME_LEVELS = ['read-only', 'approval-required', 'auto-accept-edits', 'auto', 'full-access'] as const;

/** Where a level sits in T3_RUNTIME_LEVELS; -1 when unknown. */
export function runtimeRank(level: string | null | undefined): number {
  return level ? (T3_RUNTIME_LEVELS as readonly string[]).indexOf(level) : -1;
}

/** True when `mode` asks for more than the machine's token allows (unknown ceilings never warn). */
export function isAboveCeiling(mode: string | null | undefined, ceiling: string | null | undefined): boolean {
  const m = runtimeRank(mode);
  const c = runtimeRank(ceiling);
  return m >= 0 && c >= 0 && m > c;
}

/** The hint to raise a machine's T3 ceiling. */
export const T3_LOGIN_HINT = 'On that machine, run `sf-agent t3-login` and approve full access in T3 Code.';

/** The run number in a factory actor label ("Factory · R-12", worker/src/services/tasks/actor.ts), or null. */
export function runNumberFromActorLabel(label: string | null | undefined): number | null {
  const match = label ? /^Factory · R-(\d+)$/.exec(label) : null;
  return match ? Number(match[1]) : null;
}

/** The run a follow-up would carry on: its number and machine. */
export interface ContinuableRun {
  number: number;
  machineId: string;
  /** SF-T-209: it ran live, so its Follow up starts live too. */
  live?: boolean;
  /** SF-T-302: how it got to its PR, which its Follow up starts with too. */
  pause?: RunPause;
}

/**
 * The task's run a "carries on" flow (Follow up) would continue, or null. The same rule the AI
 * builder uses (followUpFrom in worker/src/services/runs/pickup.ts): the newest run succeeded, left
 * a T3 thread on a machine, and its PR isn't merged.
 */
export function continuableRun(
  runs: readonly RunSummary[] | undefined,
  prs: readonly { url: string; status: string }[]
): ContinuableRun | null {
  const newest = runs?.[0];
  if (!newest || newest.status !== 'succeeded' || !newest.t3_thread_id || !newest.machine) return null;
  if (newest.pr_url && prs.some((p) => p.url === newest.pr_url && p.status === 'merged')) return null;
  return { number: newest.number, machineId: newest.machine.id, ...(newest.live ? { live: true } : {}), ...(newest.pause ? { pause: newest.pause } : {}) };
}

/** T-50: the chip on a run of a feedback flow (a plan, a spec and questions; no code and no column moves): a type label, square beside the status. */
export const FEEDBACK_RUN_META: StatusMeta = { label: 'Feedback only', tone: 'info' };

/**
 * T-72: the steps a folded run shows: while it's going, the step it's on (and any others running or
 * waiting); once it's ended, the steps that failed. None for a run that succeeded: its status says so.
 */
export function foldedSteps(run: { status: RunStatus; steps: RunStep[]; current_step: string | null }) {
  if (isRunActive(run.status)) {
    return run.steps.filter((s) => s.step_id === run.current_step || s.status === 'running' || s.status === 'waiting');
  }
  return run.steps.filter((s) => s.status === 'failed');
}
